import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaBriefcase, FaGraduationCap, FaProjectDiagram, FaStar } from 'react-icons/fa';
import { PiCertificateFill } from 'react-icons/pi';
import { FaChevronDown } from 'react-icons/fa';

// Map milestone types to icons
const typeIcons = {
  job: FaBriefcase,
  education: FaGraduationCap,
  project: FaProjectDiagram,
  certificate: PiCertificateFill,
  other: FaStar
};

const TimelineItem = ({ item, index, setActiveItem, isActive }) => {
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const Icon = typeIcons[item.type] || FaStar;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
      className="relative pl-12 pb-12 last:pb-0 group"
      onMouseEnter={() => setActiveItem(index)}
    >
      {/* Vertical Line */}
      <div className="absolute left-4 top-2 h-full w-px bg-gray-200 last:h-0" />
      {/* Dot with Icon */}
      <div className="absolute left-2 top-2 w-5 h-5 rounded-full bg-gray-400 group-hover:bg-gray-900 group-hover:scale-150 transition-all duration-300 flex items-center justify-center">
        {isActive && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <Icon className="w-3 h-3 text-white" />
          </motion.div>
        )}
      </div>
      {/* Content */}
      <div>
        <p className="text-xs pt-2 text-gray-500 font-medium uppercase tracking-wider mb-2">
          {item.year}
        </p>
        <h4
          className={`text-md font-light transition-colors duration-300 ${
            isActive ? 'text-gray-900' : 'text-gray-600'
          }`}
        >
          {item.company}
        </h4>
        <h4
          className={`text-lg font-light transition-colors duration-300 ${
            isActive ? 'text-gray-900' : 'text-gray-600'
          }`}
        >
          {item.title}
        </h4>
      </div>
    </motion.div>
  );
};

const Timeline = () => {
  const [activeItem, setActiveItem] = useState(null);

const timelineData = [
  {
    year: 'Dec 2023 – Present',
    title: 'Full Stack Developer',
    company: 'Celex Ensemble',
    description: 'Designed and implemented responsive front-end interfaces and full-stack features.',
    details: 'Built data visualization dashboards, RESTful APIs, and integrated frontend (React.js), backend (Spring Boot), and NoSQL database (MongoDB).',
    type: 'job',
  },
  {
    year: 'Jan - Apr 2025',
    title: 'Custom Compiler',
    company: 'Academic Project',
    description: 'Built a basic compiler targeting Moon Processor.',
    details: 'Implemented lexical, syntactic, and semantic analysis in Java.',
    type: 'project',
  },
  {
    year: 'Sept 2023 – May 2025',
    title: 'Master of Applied Computer Science',
    company: 'Concordia University – Gina Cody School of Engineering and Computer Science',
    description: 'Focused on software engineering and development.',
    details: 'CGPA: 3.4/4.3',
    type: 'education',
  },
  {
    year: 'Jan - Apr 2024',
    title: 'JIRA Voice Assistant',
    company: 'Personal Project',
    description: 'Built a Chrome extension for voice-based Jira interaction.',
    details: 'Used JavaScript and Chrome Extension APIs to support task creation, navigation, and updates via voice.',
    type: 'project',
  },
  {
    year: 'May 2023',
    title: 'Introduction to Front-End Development',
    company: 'Coursera',
    description: 'Completed foundational front-end development course.',
    details: 'Credential ID: HC5JNK95XPMF',
    type: 'certificate',
  },
  {
    year: 'May 2023',
    title: 'Programming with JavaScript',
    company: 'Coursera',
    description: 'Covered JavaScript fundamentals and browser interaction.',
    details: 'Credential ID: U4XBPG8LXP9J',
    type: 'certificate',
  },
  {
    year: 'May 2023',
    title: 'Version Control',
    company: 'Coursera',
    description: 'Learned version control with Git and GitHub.',
    details: 'Credential ID: 9UYFYS6JRDH3',
    type: 'certificate',
  },
  {
    year: 'Jan 2023',
    title: 'Business Analysis Foundations',
    company: 'LinkedIn',
    description: 'Studied key business analysis practices and frameworks.',
    details: 'Credential available via LinkedIn Learning.',
    type: 'certificate',
  },
  {
    year: 'Jan 2023',
    title: 'Foundations of User Experience (UX) Design',
    company: 'Coursera',
    description: 'Introduced to UX design principles and research.',
    details: 'Credential ID: L3R7X6UCVVRJ',
    type: 'certificate',
  },
  {
    year: 'Dec 2022',
    title: 'Scrum Fundamentals Certified (SFC)',
    company: 'SCRUMstudy - Accreditation Body for Scrum and Agile',
    description: 'Certified in Scrum fundamentals and Agile principles.',
    details: 'Credential ID: 957538',
    type: 'certificate',
  },
  {
    year: 'Jul 2022 – Aug 2023',
    title: 'Software Tester',
    company: 'Link Administration Services Private Limited – Link Group',
    description: 'Tested PRISM software and ensured application modules functioned correctly.',
    details: 'Created test cases, logged defects, and trained in Selenium Java, Microsoft Azure, and Scrum.',
    type: 'job',
  },
  {
    year: 'Aug 2021 – May 2022',
    title: 'E-sewa App for Self-Employed Women',
    company: 'Final Year Project',
    description: 'Android app to support self-employed women with digital transactions.',
    details: 'Built with Java, Android Studio, and Firebase for real-time syncing and authentication.',
    type: 'project',
  },
  {
    year: 'Jun 2020 – Jun 2021',
    title: 'Design Head',
    company: 'ACM - DBIT',
    description: 'Led the Design Team for events and workshops.',
    details: 'Organized training sessions on Adobe Illustrator, Photoshop, Blender, and Premiere Pro.',
    type: 'job',
  },
  {
    year: 'Jun 2020 – Jun 2021',
    title: 'Event Organizer',
    company: 'Teknack Gaming Studio',
    description: 'Managed online events for ACM Technical Fest.',
    details: 'Planned, led, and executed online fest components and team responsibilities.',
    type: 'job',
  },
  {
    year: 'Jan - Apr 2021',
    title: 'Kurokami – Windows Game',
    company: 'Teknack Gaming Studio',
    description: 'Action-adventure game developed in Unreal Engine 4.',
    details: 'Focused on level design, UI/UX, and gameplay mechanics.',
    type: 'project',
  },
  {
    year: 'Jun 2019 – Jun 2020',
    title: 'Design Team Member',
    company: 'ACM - DBIT',
    description: 'Created visuals and supported event logistics.',
    details: 'Designed posters, logos, videos; assisted in setup and training for workshops.',
    type: 'job',
  },
  {
    year: 'Jan - Apr 2020',
    title: 'The Catacombs – Android Game',
    company: 'Teknack Gaming Studio',
    description: 'Published a 2D horror adventure game on Play Store.',
    details: 'Built with Unity and C#; integrated AI and resource collection.',
    type: 'project',
  },
  {
    year: 'Feb 2020',
    title: 'Learn to Code in Python 3: Programming beginner to advanced',
    company: 'Udemy',
    description: 'Comprehensive Python course from basics to advanced topics.',
    details: 'Credential ID: UC-3720889d-da45-4b1a-9f65-ac263bf76e03',
    type: 'certificate',
  },
  {
    year: 'Nov 2019 – Jun 2021',
    title: 'Game Developer',
    company: 'Teknack Gaming Studio',
    description: 'Developed multiple games using Unity and Unreal Engine.',
    details: 'Built an Android game in Unity and a Windows game in Unreal Engine 4.',
    type: 'job',
  },
  {
    year: 'Aug 2018 – Jun 2022',
    title: 'Bachelor of Engineering in Computer Engineering',
    company: 'University of Mumbai – Don Bosco Institute of Technology',
    description: 'Completed undergraduate studies in Computer Engineering.',
    details: 'CGPA: 8.54/10.00',
    type: 'education',
  }
];


  return (
    <section className="px-4 sm:px-6 lg:px-8 mb-8 overflow-hidden">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12 text-center">
          <h3 className="text-xs text-gray-500 font-medium mb-4 uppercase tracking-wider">
            Professional Journey
          </h3>
          <h2 className="text-4xl font-light text-gray-900">
            Career Timeline
          </h2>
        </div>

        {/* Timeline and Content */}
<div className="flex flex-col lg:flex-row gap-8">
  {/* Left: Scrollable Timeline */}
  <div className="w-full lg:w-1/3">

    <div className="h-[80vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 pr-4">
      {timelineData.map((item, index) => (
        <TimelineItem
          key={index}
          item={item}
          index={index}
          setActiveItem={setActiveItem}
          isActive={activeItem === index}
        />
      ))}
    </div>
     {/* Scroll prompt arrow */}
    <div className="flex justify-center mb-2">
      <div className="animate-bounce text-gray-500">
        <FaChevronDown className="w-5 h-5" />
      </div>
    </div>
  </div>

  {/* Right: Static Hover Content */}
  <div className="w-full lg:w-2/3">
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 10 }}
    transition={{ duration: 0.3 }}
    className="text-lg text-gray-600 sticky top-0"
  >
    {activeItem !== null ? (
      <>
        <h4 className="text-lg font-light text-gray-900 mb-2">
          {timelineData[activeItem].title} ({timelineData[activeItem].year})
        </h4>
        <p className="mb-4">{timelineData[activeItem].description}</p>
        <p>{timelineData[activeItem].details}</p>
      </>
    ) : (
      <>
        <h4 className="text-lg font-light text-gray-900 mb-2">Hover over a timeline entry</h4>
        <p className="mb-4">Discover more about my professional journey by hovering over the milestones on the left.</p>
        <p>Each entry reveals a deeper insight into my experience, education, and projects.</p>
      </>
    )}
  </motion.div>
</div>

</div>

      </div>
    </section>
  );
};

export default Timeline;